import React from 'react';
import { SignIn } from '@clerk/clerk-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import SuccessStories from './components/SuccessStories';
import TrustSignals from './components/TrustSignals';

const UserLogin = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
              
              {/* Left Side - Welcome Back Message & Benefits */}
              <div className="space-y-6 lg:pr-8">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
                    <Icon name="Sparkles" size={16} />
                    <span className="text-sm font-medium">Welcome Back</span>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4 leading-tight">
                    Continue Your
                    <span className="block text-primary">China Journey</span>
                  </h1>
                  
                  <p className="text-base text-muted-foreground font-caption leading-relaxed mb-6">
                    Sign in to access your applications, track your progress, and connect with your dream university.
                  </p>
                </div>

                {/* Success Stories Component */}
                <div className="hidden lg:block">
                  <SuccessStories />
                </div>

                {/* Trust Signals - Hidden to reduce height */}
                <div className="hidden">
                  <TrustSignals />
                </div>
              </div>

              {/* Right Side - Sign In Form */}
              <div className="relative">
                {/* Decorative Background Elements */}
                <div className="absolute -top-4 -right-4 w-72 h-72 bg-primary/5 rounded-full blur-3xl -z-10"></div>
                <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-accent/5 rounded-full blur-3xl -z-10"></div>
                
                <div className="bg-card rounded-3xl shadow-2xl border border-border overflow-hidden">
                  <div className="p-8 md:p-10">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                        Sign In to Your Account
                      </h2>
                      <p className="text-sm text-muted-foreground font-caption">
                        Access your dashboard and continue your application
                      </p>
                    </div>

                    {/* Clerk Sign In Component */}
                    <div className="flex items-center justify-center w-full min-h-[400px]">
                      <div className="w-full flex justify-center">
                        <SignIn 
                          signUpUrl="/user-registration"
                          afterSignInUrl="/user-dashboard"
                          redirectUrl="/user-dashboard"
                          appearance={{
                            elements: {
                              rootBox: "mx-auto",
                              card: "shadow-none bg-transparent mx-auto",
                              formButtonPrimary: "bg-primary hover:bg-primary/90 text-sm normal-case",
                              formFieldInput: "rounded-lg",
                              footerActionLink: "text-primary hover:text-primary/80"
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Info Bar */}
                  <div className="bg-muted/30 px-8 py-4 border-t border-border">
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Shield" size={14} />
                      <span>Your data is protected with 256-bit SSL encryption</span>
                    </div>
                  </div>
                </div>

                {/* Help Section - Moved below login form */}
                <div className="mt-6">
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <p className="text-sm font-semibold text-foreground mb-4 text-center">Need assistance?</p>
                    <div className="flex items-center justify-center gap-4 flex-wrap">
                      <a href="mailto:support@chineselangapp.com" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                        <Icon name="Mail" size={16} />
                        Email Support
                      </a>
                      <span className="text-muted-foreground">•</span>
                      <a href="tel:+8618810799128" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                        <Icon name="Phone" size={16} />
                        Call Us
                      </a>
                      <span className="text-muted-foreground">•</span>
                      <button className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                        <Icon name="MessageCircle" size={16} />
                        Live Chat
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Mobile Success Stories & Trust Signals */}
        <div className="lg:hidden max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pb-12 space-y-8">
          <SuccessStories />
          <TrustSignals />
        </div>

        {/* Support Section - Redesigned */}
        <div className="bg-gradient-to-r from-primary/5 via-background to-accent/5 border-t border-border py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
                Need Help Getting Started?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground font-caption max-w-2xl mx-auto">
                Our support team is available 24/7 to assist you with any questions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-2xl p-6 text-center border border-border hover:shadow-lg transition-smooth group">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-smooth">
                  <Icon name="MessageCircle" size={28} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                  Live Chat
                </h3>
                <p className="text-sm text-muted-foreground font-caption mb-4">
                  Get instant help from our support team
                </p>
                <button className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth font-caption">
                  Start Chat →
                </button>
              </div>

              <div className="bg-card rounded-2xl p-6 text-center border border-border hover:shadow-lg transition-smooth group">
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-smooth">
                  <Icon name="Mail" size={28} color="var(--color-accent)" />
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                  Email Support
                </h3>
                <p className="text-sm text-muted-foreground font-caption mb-4">
                  support@chineselangapp.com
                </p>
                <button className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth font-caption">
                  Send Email →
                </button>
              </div>

              <div className="bg-card rounded-2xl p-6 text-center border border-border hover:shadow-lg transition-smooth group">
                <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary/20 transition-smooth">
                  <Icon name="Phone" size={28} color="var(--color-secondary)" />
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                  Phone Support
                </h3>
                <p className="text-sm text-muted-foreground font-caption mb-4">
                  +86 188 1079 9128
                </p>
                <button className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth font-caption">
                  Call Now →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UserLogin;